import type {
  Article,
  AuthenticatedUser,
  Fixture,
  Gallery,
  GalleryItem,
  LeagueTable,
  MatchDetail,
  MatchLiveState,
  Player,
  Sponsor,
  StaffMember,
  Stream,
  Team,
} from "@/types/domain";
import { mockAssets } from "./assets";

/**
 * Comprehensive sample dataset for the Soweto Super United FC frontend.
 * Everything routes through the mock adapter (VITE_USE_MOCK_API=true) and
 * is refused in production by src/mocks/guard.ts. Records include a
 * __sample marker for admin surfaces to render "Sample" chips.
 */

const now = new Date();
const iso = (offsetMinutes: number) =>
  new Date(now.getTime() + offsetMinutes * 60_000).toISOString();

// ---- Roles / permissions ------------------------------------------------
function ALL_PERMISSIONS() {
  return [
    "players.view",
    "players.create",
    "players.edit",
    "players.view_private",
    "teams.manage",
    "staff.manage",
    "fixtures.manage",
    "matches.control",
    "streams.manage",
    "articles.publish",
    "articles.manage",
    "media.upload",
    "media.manage",
    "galleries.manage",
    "sponsors.manage",
    "users.manage",
    "roles.manage",
    "permissions.view",
    "settings.manage",
    "reports.view",
    "audit.view",
    "coaching.view",
    "training.manage",
    "selections.manage",
    "forms.manage",
    "enquiries.manage",
  ];
}

const demoUsers: Record<string, AuthenticatedUser> = {
  admin: {
    publicId: "usr_admin_sample",
    name: "Thabo Ndlovu",
    email: "admin@sample.local",
    emailVerifiedAt: iso(-60 * 24 * 30),
    status: "active",
    roles: ["super_administrator"],
    permissions: ALL_PERMISSIONS(),
    teamAccess: ["team_first_sample"],
    profile: { firstName: "Thabo", lastName: "Ndlovu", avatarUrl: mockAssets.staffPortrait(0) },
  },
  coach: {
    publicId: "usr_coach_sample",
    name: "Sipho Mokoena",
    email: "coach@sample.local",
    emailVerifiedAt: iso(-60 * 24 * 30),
    status: "active",
    roles: ["head_coach"],
    permissions: [
      "coaching.view",
      "training.manage",
      "selections.manage",
      "players.view",
      "players.view_private",
      "reports.view",
    ],
    teamAccess: ["team_first_sample"],
    profile: { firstName: "Sipho", lastName: "Mokoena", avatarUrl: mockAssets.staffPortrait(1) },
  },
  player: {
    publicId: "usr_player_sample",
    name: "Kagiso Dlamini",
    email: "player@sample.local",
    emailVerifiedAt: iso(-60 * 24 * 30),
    status: "active",
    roles: ["player"],
    permissions: ["player.self.view", "player.self.edit"],
    teamAccess: ["team_first_sample"],
    profile: { firstName: "Kagiso", lastName: "Dlamini", avatarUrl: mockAssets.portrait(0) },
  },
  matchop: {
    publicId: "usr_matchop_sample",
    name: "Nomsa Khumalo",
    email: "matchops@sample.local",
    emailVerifiedAt: iso(-60 * 24 * 20),
    status: "active",
    roles: ["match_operator", "stream_operator"],
    permissions: ["matches.control", "streams.manage"],
    teamAccess: ["team_first_sample"],
    profile: { firstName: "Nomsa", lastName: "Khumalo", avatarUrl: mockAssets.staffPortrait(3) },
  },
};

// ---- Teams --------------------------------------------------------------
const teams: Team[] = [
  {
    publicId: "team_first_sample",
    name: "First Team",
    slug: "first-team",
    ageGroup: "Senior",
    gender: "male",
    description:
      "Soweto Super United FC senior men's team, competing in the Regional Premier League.",
  },
  {
    publicId: "team_reserves_sample",
    name: "Reserves",
    slug: "reserves",
    ageGroup: "Senior",
    gender: "male",
    description: "Reserve squad, providing match-day depth and development pathways.",
  },
  {
    publicId: "team_u19_sample",
    name: "U19 Development",
    slug: "u19",
    ageGroup: "U19",
    gender: "male",
    description: "Youth development squad focused on producing First Team-ready graduates.",
  },
  {
    publicId: "team_u17_sample",
    name: "U17 Academy",
    slug: "u17",
    ageGroup: "U17",
    gender: "male",
    description:
      "Academy programme partnering with Soweto schools to develop technical footballers.",
  },
];

// ---- Players ------------------------------------------------------------
const PLAYER_ROSTER: Array<{
  first: string;
  last: string;
  number: number;
  position: string;
  team?: string;
  captain?: boolean;
  height?: number;
  nationality?: string;
}> = [
  {
    first: "Sanele",
    last: "Mabaso",
    number: 1,
    position: "GK",
    nationality: "South African",
    height: 190,
  },
  {
    first: "Junior",
    last: "Radebe",
    number: 2,
    position: "RB",
    nationality: "South African",
    height: 178,
  },
  {
    first: "Thulani",
    last: "Msibi",
    number: 3,
    position: "CB",
    nationality: "South African",
    height: 187,
  },
  {
    first: "Mandla",
    last: "Zulu",
    number: 4,
    position: "CB",
    captain: true,
    nationality: "South African",
    height: 189,
  },
  {
    first: "Bafana",
    last: "Sithole",
    number: 5,
    position: "LB",
    nationality: "South African",
    height: 180,
  },
  {
    first: "Kagiso",
    last: "Dlamini",
    number: 6,
    position: "CDM",
    nationality: "South African",
    height: 182,
  },
  {
    first: "Sipho",
    last: "Ndlovu",
    number: 7,
    position: "RW",
    nationality: "South African",
    height: 174,
  },
  {
    first: "Themba",
    last: "Mokoena",
    number: 8,
    position: "CM",
    nationality: "South African",
    height: 179,
  },
  {
    first: "Lucky",
    last: "Nkosi",
    number: 9,
    position: "ST",
    nationality: "South African",
    height: 184,
  },
  {
    first: "Katlego",
    last: "Modise",
    number: 10,
    position: "CAM",
    nationality: "South African",
    height: 176,
  },
  {
    first: "Bongani",
    last: "Khumalo",
    number: 11,
    position: "LW",
    nationality: "South African",
    height: 175,
  },
  {
    first: "Vusi",
    last: "Mahlangu",
    number: 12,
    position: "GK",
    nationality: "South African",
    height: 188,
  },
  {
    first: "Tshepo",
    last: "Molefe",
    number: 13,
    position: "RB",
    nationality: "South African",
    height: 177,
  },
  {
    first: "Neo",
    last: "Radebe",
    number: 14,
    position: "CB",
    nationality: "South African",
    height: 186,
  },
  {
    first: "Tumelo",
    last: "Mabena",
    number: 15,
    position: "CB",
    nationality: "South African",
    height: 185,
  },
  {
    first: "Refilwe",
    last: "Motaung",
    number: 16,
    position: "LB",
    nationality: "South African",
    height: 179,
  },
  {
    first: "Nkosinathi",
    last: "Zwane",
    number: 17,
    position: "CM",
    nationality: "South African",
    height: 181,
  },
  {
    first: "Andile",
    last: "Mahlaba",
    number: 18,
    position: "CM",
    nationality: "South African",
    height: 178,
  },
  {
    first: "Kabelo",
    last: "Rakoena",
    number: 19,
    position: "RW",
    nationality: "South African",
    height: 173,
  },
  {
    first: "Percy",
    last: "Sibiya",
    number: 20,
    position: "ST",
    nationality: "South African",
    height: 183,
  },
  {
    first: "Owethu",
    last: "Mpofu",
    number: 21,
    position: "LW",
    nationality: "Zimbabwean",
    height: 177,
  },
  {
    first: "Mzwakhe",
    last: "Buthelezi",
    number: 22,
    position: "CAM",
    nationality: "South African",
    height: 178,
  },
  {
    first: "Given",
    last: "Nkuna",
    number: 23,
    position: "ST",
    nationality: "Mozambican",
    height: 186,
  },
  {
    first: "Simiso",
    last: "Ntuli",
    number: 24,
    position: "CDM",
    nationality: "South African",
    height: 181,
  },
  {
    first: "Kwanele",
    last: "Shezi",
    number: 25,
    position: "GK",
    nationality: "South African",
    height: 191,
  },
];

const players: Player[] = PLAYER_ROSTER.map((p, i) => ({
  publicId: `plr_${p.number}_sample`,
  firstName: p.first,
  lastName: p.last,
  displayName: `${p.first} ${p.last}`,
  slug: `${p.first}-${p.last}`.toLowerCase().replace(/\s+/g, "-"),
  shirtNumber: p.number,
  position: p.position,
  photoUrl: mockAssets.portrait(i),
  teamPublicId: p.number <= 22 ? "team_first_sample" : "team_reserves_sample",
  isCaptain: !!p.captain,
  heightCm: p.height,
  nationality: p.nationality,
  dateOfBirth: `199${3 + (i % 6)}-${String(1 + (i % 12)).padStart(2, "0")}-${String(1 + (i % 27)).padStart(2, "0")}`,
  bio: `${p.first} ${p.last} is a ${p.position === "GK" ? "commanding goalkeeper" : p.position.includes("B") ? "aggressive defender" : p.position.includes("M") ? "intelligent midfielder" : "clinical forward"} who came through the Soweto development system. Known for consistency, professionalism and a total commitment to the club shirt.`,
}));

// ---- Staff --------------------------------------------------------------
const staff: StaffMember[] = [
  {
    publicId: "stf_hc",
    name: "Sipho Mokoena",
    role: "Head Coach",
    teamPublicId: "team_first_sample",
    photoUrl: mockAssets.staffPortrait(1),
    bio: "Former national team midfielder. Fifteen years of top-flight coaching experience.",
  },
  {
    publicId: "stf_ac",
    name: "Vusi Zulu",
    role: "Assistant Coach",
    teamPublicId: "team_first_sample",
    photoUrl: mockAssets.staffPortrait(2),
    bio: "UEFA Pro Licence. Specialises in transition play and set pieces.",
  },
  {
    publicId: "stf_gk",
    name: "Peter van Wyk",
    role: "Goalkeeper Coach",
    teamPublicId: "team_first_sample",
    photoUrl: mockAssets.staffPortrait(4),
    bio: "Ex-professional keeper. Twenty international caps.",
  },
  {
    publicId: "stf_fitness",
    name: "Zanele Mthembu",
    role: "Head of Sport Science",
    teamPublicId: "team_first_sample",
    photoUrl: mockAssets.staffPortrait(5),
    bio: "MSc Sport Science. Leads performance analytics and load management.",
  },
  {
    publicId: "stf_physio",
    name: "Ricardo Pereira",
    role: "Head Physiotherapist",
    teamPublicId: "team_first_sample",
    photoUrl: mockAssets.staffPortrait(6),
    bio: "Sports physiotherapist with a decade of professional experience.",
  },
  {
    publicId: "stf_ceo",
    name: "Naledi Khumalo",
    role: "Chief Executive",
    photoUrl: mockAssets.staffPortrait(0),
    bio: "Executive leadership across sport, commercial and community operations.",
  },
  {
    publicId: "stf_academy",
    name: "Bheki Ngwenya",
    role: "Academy Director",
    teamPublicId: "team_u17_sample",
    photoUrl: mockAssets.staffPortrait(3),
    bio: "Oversees the U15–U19 pathway and school partnerships.",
  },
  {
    publicId: "stf_media",
    name: "Nadia Petersen",
    role: "Head of Media",
    photoUrl: mockAssets.staffPortrait(7),
    bio: "Broadcast producer. Leads editorial, video and matchday content.",
  },
];

// ---- Fixtures / results -------------------------------------------------
const OPPONENTS = [
  "Orlando Athletic",
  "Protea City FC",
  "Meadowlands United",
  "Dobsonville Rovers",
  "Diepkloof Stars",
  "Pimville Sporting",
  "Kliptown City",
  "Mofolo Wanderers",
  "Jabulani Athletic",
  "Zola United",
  "Dube Continental",
  "Mapetla Rangers",
  "Naledi Sporting",
  "Senaoane City",
  "Rockville FC",
];

const CLUB_NAME = "Soweto Super United FC";
const VENUES_HOME = ["Soweto Stadium", "Dobsonville Sports Centre"];
const VENUES_AWAY = [
  "Rockville Arena",
  "Diepkloof Municipal Ground",
  "Orlando East Stadium",
  "Kliptown Sportsfield",
];

// One current live match
const liveFixture: Fixture = {
  publicId: "fx_live_sample",
  competition: "Regional Premier League",
  round: "Matchday 13",
  scheduledStart: iso(-55),
  venue: "Soweto Stadium",
  isHome: true,
  homeTeam: { name: CLUB_NAME, publicId: "team_first_sample" },
  awayTeam: { name: "Orlando Athletic" },
  status: "live",
  score: { home: 2, away: 1 },
  streamPublicId: "stm_live_sample",
};

// 12 upcoming fixtures
const upcomingFixtures: Fixture[] = Array.from({ length: 12 }, (_, i) => {
  const isHome = i % 2 === 0;
  const opp = OPPONENTS[(i + 1) % OPPONENTS.length];
  return {
    publicId: `fx_up_${i}_sample`,
    competition: i < 9 ? "Regional Premier League" : "Provincial Cup",
    round: i < 9 ? `Matchday ${14 + i}` : `Round of ${16 / Math.max(1, i - 8)}`,
    scheduledStart: iso(60 * 24 * (i + 3) + (i % 3) * 90),
    venue: isHome ? VENUES_HOME[i % VENUES_HOME.length] : VENUES_AWAY[i % VENUES_AWAY.length],
    isHome,
    homeTeam: {
      name: isHome ? CLUB_NAME : opp,
      publicId: isHome ? "team_first_sample" : undefined,
    },
    awayTeam: {
      name: isHome ? opp : CLUB_NAME,
      publicId: isHome ? undefined : "team_first_sample",
    },
    status: "scheduled",
    score: null,
    streamPublicId: i < 3 ? `stm_up_${i}_sample` : null,
  };
});

// 20 completed results
const completedResults: Fixture[] = Array.from({ length: 20 }, (_, i) => {
  const isHome = i % 2 === 0;
  const opp = OPPONENTS[i % OPPONENTS.length];
  const homeScore = isHome ? (i % 4) + 1 : i % 3;
  const awayScore = isHome ? i % 2 : i % 4;
  return {
    publicId: `fx_res_${i}_sample`,
    competition: i < 15 ? "Regional Premier League" : "Provincial Cup",
    round: i < 15 ? `Matchday ${12 - i}` : `Round of ${32}`,
    scheduledStart: iso(-60 * 24 * (i + 3)),
    venue: isHome ? VENUES_HOME[i % VENUES_HOME.length] : VENUES_AWAY[i % VENUES_AWAY.length],
    isHome,
    homeTeam: {
      name: isHome ? CLUB_NAME : opp,
      publicId: isHome ? "team_first_sample" : undefined,
    },
    awayTeam: {
      name: isHome ? opp : CLUB_NAME,
      publicId: isHome ? undefined : "team_first_sample",
    },
    status: "full_time",
    score: { home: homeScore, away: awayScore },
  };
});

const fixtures = [liveFixture, ...upcomingFixtures];
const results = completedResults;

// ---- Streams ------------------------------------------------------------
const streams: Stream[] = [
  {
    publicId: "stm_live_sample",
    title: `${CLUB_NAME} vs Orlando Athletic — Live`,
    description: "Regional Premier League — Matchday 13. Full 90 minutes with expert commentary.",
    status: "live",
    playbackType: "hls",
    embedUrl: null,
    hlsUrl: "https://test-streams.mux.dev/x36xhzz/x36xhzz.m3u8",
    posterUrl: mockAssets.hero[0],
    scheduledStart: iso(-70),
    actualStart: iso(-55),
    actualEnd: null,
    visibility: "public",
    fixture: {
      publicId: liveFixture.publicId,
      homeTeam: liveFixture.homeTeam,
      awayTeam: liveFixture.awayTeam,
      scheduledStart: liveFixture.scheduledStart,
    },
    sponsor: { name: "Rand Central Bank", logoUrl: mockAssets.sponsor("Rand Central") },
    viewerCount: 1287,
    replayUrl: null,
    highlightUrl: null,
  },
  {
    publicId: "stm_up_0_sample",
    title: `${CLUB_NAME} vs Protea City FC`,
    description: "Regional Premier League — Matchday 14 live broadcast.",
    status: "scheduled",
    playbackType: "hls",
    embedUrl: null,
    hlsUrl: null,
    posterUrl: mockAssets.hero[1],
    scheduledStart: iso(60 * 24 * 3),
    actualStart: null,
    actualEnd: null,
    visibility: "public",
    fixture: null,
    sponsor: null,
    viewerCount: null,
    replayUrl: null,
    highlightUrl: null,
  },
  {
    publicId: "stm_replay_1_sample",
    title: `Meadowlands United vs ${CLUB_NAME} — Replay`,
    description: "Full replay from last weekend's away trip.",
    status: "replay_available",
    playbackType: "hls",
    embedUrl: null,
    hlsUrl: "https://test-streams.mux.dev/x36xhzz/x36xhzz.m3u8",
    posterUrl: mockAssets.hero[2],
    scheduledStart: iso(-60 * 24 * 7),
    actualStart: iso(-60 * 24 * 7),
    actualEnd: iso(-60 * 24 * 7 + 120),
    visibility: "public",
    fixture: null,
    sponsor: null,
    viewerCount: null,
    replayUrl: "https://test-streams.mux.dev/x36xhzz/x36xhzz.m3u8",
    highlightUrl: null,
  },
  {
    publicId: "stm_replay_2_sample",
    title: "Dobsonville Rovers Highlights",
    description: "Match highlights package.",
    status: "replay_available",
    playbackType: "hls",
    embedUrl: null,
    hlsUrl: "https://test-streams.mux.dev/x36xhzz/x36xhzz.m3u8",
    posterUrl: mockAssets.hero[3],
    scheduledStart: iso(-60 * 24 * 14),
    actualStart: iso(-60 * 24 * 14),
    actualEnd: iso(-60 * 24 * 14 + 8),
    visibility: "public",
    fixture: null,
    sponsor: null,
    viewerCount: null,
    replayUrl: "https://test-streams.mux.dev/x36xhzz/x36xhzz.m3u8",
    highlightUrl: null,
  },
];

// ---- Articles / news ----------------------------------------------------
const ARTICLE_TITLES = [
  { t: "Champions of Soweto: how this squad has redefined the club", c: "Feature" },
  { t: "Match preview: Orlando Athletic under Soweto lights", c: "Match preview" },
  { t: "Dlamini extends contract in landmark deal for the club", c: "Club news" },
  { t: "Academy report: three U17 graduates called into First Team training", c: "Academy" },
  { t: "Ticket news: Provincial Cup tie sold out in 45 minutes", c: "Tickets" },
  { t: "Community: Super United Foundation launches new schools programme", c: "Community" },
  { t: "Interview: Zulu on captaincy, culture and the road ahead", c: "Interview" },
  { t: "Full time: five things we learned from Meadowlands", c: "Analysis" },
  { t: "Injury update: Mokoena expected back within three weeks", c: "Team news" },
  { t: "Kit launch: 2025/26 home shirt unveiled", c: "Club news" },
  { t: "How the Reserves are reshaping the pathway to the First Team", c: "Feature" },
  { t: "Board announces new commercial partnership with Rand Central Bank", c: "Business" },
  { t: "Coach Mokoena: 'We will earn every point'", c: "Interview" },
  { t: "Scouting report: the tactical shift powering our unbeaten run", c: "Analysis" },
  { t: "Fans forum: your questions answered by the technical staff", c: "Fans" },
];

const articles: Article[] = ARTICLE_TITLES.map((a, i) => ({
  publicId: `art_${i}_sample`,
  slug: a.t
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, ""),
  title: a.t,
  excerpt:
    "Sample editorial preview. Replace once the CMS is connected to the production PHP API. This paragraph gives readers a taste of the article and encourages the click through to the full read.",
  body: `<p>This is the opening paragraph of a sample article for Soweto Super United FC. In production, the PHP CMS delivers rich HTML including embedded images, blockquotes and inline media.</p>
<p>Our writers cover matchdays, transfers, the academy, community projects and the commercial life of the club. Every article is signed off by the editorial team.</p>
<blockquote>“The badge belongs to Soweto. Every performance has to reflect that.” — Head Coach Sipho Mokoena</blockquote>
<p>Read on for the full write-up including tactical breakdowns, player quotes and what it all means heading into the weekend fixture.</p>`,
  coverImageUrl: mockAssets.gallery(i),
  authorName: ["Nadia Petersen", "Editor's Desk", "Kabelo Mahlangu", "Sindiswa Cele"][i % 4],
  publishedAt: iso(-60 * (i * 6 + 3)),
  category: a.c,
  tags: ["sample", a.c.toLowerCase()],
  readingMinutes: 2 + (i % 6),
}));

// ---- Videos & Highlights ------------------------------------------------
const videos = Array.from({ length: 18 }, (_, i) => {
  const isHighlight = i < 8;
  return {
    publicId: `vid_${i}_sample`,
    title: isHighlight
      ? `HIGHLIGHTS: ${CLUB_NAME} ${(i % 4) + 1}-${i % 3} ${OPPONENTS[i % OPPONENTS.length]}`
      : [
          "Behind the scenes: matchday walkthrough",
          "Training ground: set-piece session",
          "Academy showcase",
          "Fans: the derby atmosphere",
          "Coach Mokoena press conference",
          "Kit reveal",
          "Player mic'd up",
          "Academy graduation ceremony",
          "Sponsors: Rand Central partnership film",
          "Community day at Kliptown Primary",
        ][i % 10],
    duration: isHighlight ? 240 + i * 15 : 60 + i * 20,
    category: isHighlight
      ? "Highlights"
      : ["Behind the scenes", "Training", "Academy", "Interviews", "Community", "Sponsors"][i % 6],
    publishedAt: iso(-60 * 24 * (i + 1)),
    viewCount: 1200 + i * 830,
    thumbnailUrl: mockAssets.gallery((i + 3) % 15),
    matchPublicId: isHighlight ? `fx_res_${i}_sample` : null,
    playerPublicId: !isHighlight && i % 3 === 0 ? `plr_${i + 1}_sample` : null,
    hlsUrl: "https://test-streams.mux.dev/x36xhzz/x36xhzz.m3u8",
  };
});

// ---- Galleries ----------------------------------------------------------
const galleries: Gallery[] = Array.from({ length: 8 }, (_, g) => {
  const items: GalleryItem[] = Array.from({ length: 8 }, (_, i) => ({
    publicId: `pho_${g}_${i}`,
    imageUrl: mockAssets.gallery((g * 8 + i) % 15),
    thumbnailUrl: mockAssets.gallery((g * 8 + i) % 15),
    caption: [
      "The travelling supporters make themselves heard",
      "Captain Mandla Zulu lifts the derby",
      "Half-time in the technical area",
      "The Soweto Stadium tunnel",
      "Warm-up before kick-off",
      "Full-time embrace",
      "Academy graduation night",
      "Community outreach in Kliptown",
    ][i],
    credit: "Nadia Petersen / SSUFC Media",
    altText: `Sample photograph ${i + 1} from Soweto Super United FC.`,
  }));
  return {
    publicId: `gal_${g}_sample`,
    title: [
      "Matchday: Orlando Athletic",
      "Training ground: pre-season",
      "Academy showcase evening",
      "Kit launch 2025/26",
      "Fans at Dobsonville",
      "Community day",
      "Behind the scenes: matchday",
      "Away trip: Meadowlands",
    ][g],
    description:
      "Curated selection from our media team. High-resolution images available for accredited press.",
    coverImageUrl: mockAssets.gallery((g * 8) % 15),
    photoCount: 8,
    publishedAt: iso(-60 * 24 * (g * 3 + 2)),
    items,
  };
});

// ---- Sponsors -----------------------------------------------------------
const sponsors: Sponsor[] = [
  {
    publicId: "spn_principal",
    name: "Rand Central Bank",
    tier: "principal",
    logoUrl: mockAssets.sponsor("Rand Central"),
    websiteUrl: "#",
    description: "Principal partner powering our matchday and community programmes.",
  },
  {
    publicId: "spn_platinum",
    name: "Soweto Motors Group",
    tier: "platinum",
    logoUrl: mockAssets.sponsor("Soweto Motors"),
    websiteUrl: "#",
    description: "Official mobility partner.",
  },
  {
    publicId: "spn_gold_1",
    name: "Kliptown Energy",
    tier: "gold",
    logoUrl: mockAssets.sponsor("Kliptown Energy"),
    websiteUrl: "#",
    description: "Official energy partner.",
  },
  {
    publicId: "spn_gold_2",
    name: "Vuka Insurance",
    tier: "gold",
    logoUrl: mockAssets.sponsor("Vuka Insurance"),
    websiteUrl: "#",
    description: "Official insurance partner.",
  },
  {
    publicId: "spn_silver",
    name: "Baobab Media",
    tier: "silver",
    logoUrl: mockAssets.sponsor("Baobab Media"),
    websiteUrl: "#",
    description: "Broadcast production partner.",
  },
  {
    publicId: "spn_partner",
    name: "Meadowlands Hospitality",
    tier: "partner",
    logoUrl: mockAssets.sponsor("Meadowlands"),
    websiteUrl: "#",
    description: "Official hospitality partner.",
  },
];

// ---- League table (16 teams) --------------------------------------------
const LEAGUE_TEAMS = [CLUB_NAME, ...OPPONENTS.slice(0, 15)];
const leagueTables: LeagueTable[] = [
  {
    competition: "Regional Premier League",
    season: "2025/26",
    updatedAt: iso(-60),
    rows: LEAGUE_TEAMS.slice(0, 16)
      .map((team, i) => {
        const isClub = team === CLUB_NAME;
        const played = 12;
        const won = isClub ? 9 : Math.max(0, 10 - i);
        const drawn = Math.max(0, 3 - (i % 4));
        const lost = played - won - drawn;
        const gf = won * 2 + drawn + (isClub ? 4 : 0);
        const ga = lost * 2 + Math.floor(drawn / 2);
        return {
          position: 0,
          team,
          played,
          won: Math.max(0, won),
          drawn: Math.max(0, drawn),
          lost: Math.max(0, lost),
          goalsFor: gf,
          goalsAgainst: ga,
          goalDifference: gf - ga,
          points: won * 3 + drawn,
          form: (["W", "W", "D", "W", "L"] as const).map((f, k) => (isClub && k === 4 ? "W" : f)),
          isClub,
        };
      })
      .sort((a, b) => b.points - a.points || b.goalDifference - a.goalDifference)
      .map((row, i) => ({ ...row, position: i + 1 })),
  },
];

// ---- Home page composition ---------------------------------------------
const settings = {
  __sample: true,
  clubName: CLUB_NAME,
  primaryColor: "#071A3A",
  accentColor: "#D9A328",
  contactEmail: "info@sowetosuperunitedfc.com",
};

const navigation = {
  __sample: true,
  primary: [
    { label: "News", href: "/news" },
    { label: "Fixtures", href: "/fixtures" },
    { label: "Results", href: "/results" },
    { label: "Teams", href: "/teams" },
    { label: "Watch Live", href: "/watch" },
    { label: "Videos", href: "/videos" },
    { label: "Galleries", href: "/galleries" },
    { label: "About", href: "/about" },
    { label: "Sponsors", href: "/sponsors" },
    { label: "Contact", href: "/contact" },
  ],
};

const home = {
  __sample: true,
  hero: {
    title: "RISE. PLAY. BELONG.",
    subtitle: "Official home of Soweto Super United FC — the pride of Soweto.",
    ctaLabel: "Watch Live",
    ctaHref: "/watch",
  },
  liveFixture,
  featuredArticles: articles.slice(0, 4),
  sponsors,
};

// ---- Portal users list --------------------------------------------------
const PORTAL_USERS = Array.from({ length: 20 }, (_, i) => {
  const roles = [
    ["super_administrator"],
    ["club_administrator"],
    ["team_administrator"],
    ["head_coach"],
    ["assistant_coach"],
    ["team_manager"],
    ["media_manager"],
    ["content_editor"],
    ["photographer"],
    ["match_operator"],
    ["stream_operator"],
    ["analyst"],
    ["player"],
    ["player"],
    ["player"],
    ["player"],
    ["staff_member"],
    ["sponsor_representative"],
    ["registered_supporter"],
    ["custom"],
  ][i];
  return {
    publicId: `usr_${i}_sample`,
    name: `${PLAYER_ROSTER[i % PLAYER_ROSTER.length].first} ${PLAYER_ROSTER[i % PLAYER_ROSTER.length].last}`,
    email: `user${i + 1}@sample.local`,
    roles,
    status: (["active", "active", "active", "suspended", "pending_verification"] as const)[i % 5],
    lastLoginAt: iso(-60 * (i + 1)),
    teamAccess: ["team_first_sample"],
  };
});

// ---- Training / attendance / assessments -------------------------------
const trainingSessions = Array.from({ length: 10 }, (_, i) => ({
  publicId: `trn_${i}_sample`,
  title: [
    "Recovery + video review",
    "Tactical: build-up shape",
    "Set pieces (attacking)",
    "High-intensity intervals",
    "Match prep: opposition analysis",
    "Small-sided games",
    "Strength & conditioning",
    "Team shape (defensive)",
    "Finishing session",
    "Recovery + mobility",
  ][i],
  scheduledStart: iso(60 * 24 * (i - 5) + (i % 2) * 90),
  duration: 90,
  location: i % 2 === 0 ? "Soweto Stadium — Pitch B" : "Dobsonville Training Centre",
  team: "First Team",
  requiredEquipment: ["Boots", "Bibs", "Water"],
  notes: "Report 15 minutes before start. Punctuality is non-negotiable.",
}));

const trainingAttendance = trainingSessions.flatMap((s, si) =>
  players.slice(0, 22).map((p, pi) => ({
    sessionPublicId: s.publicId,
    playerPublicId: p.publicId,
    status: ((si + pi) % 9 === 0 ? "absent" : (si + pi) % 6 === 0 ? "late" : "present") as
      "present" | "late" | "absent",
  })),
);

const playerAssessments = players.slice(0, 12).map((p, i) => ({
  publicId: `ass_${i}_sample`,
  playerPublicId: p.publicId,
  rating: 6 + ((i * 3) % 4) * 0.5,
  category: ["Technical", "Physical", "Tactical", "Mental"][i % 4],
  summary: "Strong week. Continues to raise standards for the group.",
  createdBy: "Sipho Mokoena",
  createdAt: iso(-60 * 24 * (i + 1)),
}));

// ---- Match live commentary / events ------------------------------------
const LIVE_EVENTS = [
  {
    minute: 12,
    type: "goal",
    team: "home",
    playerName: "Kagiso Dlamini",
    detail: "Wonderful strike from the edge of the box.",
  },
  {
    minute: 27,
    type: "yellow_card",
    team: "away",
    playerName: "Away captain",
    detail: "Late tackle in midfield.",
  },
  {
    minute: 34,
    type: "goal",
    team: "away",
    playerName: "Away Striker",
    detail: "Clinical finish after a defensive lapse.",
  },
  { minute: 45, type: "half_time", team: "home" },
  {
    minute: 58,
    type: "substitution",
    team: "home",
    playerName: "Percy Sibiya",
    detail: "On for Lucky Nkosi.",
  },
  {
    minute: 63,
    type: "goal",
    team: "home",
    playerName: "Sipho Ndlovu",
    detail: "Reacts fastest to a rebound.",
  },
];

// ---- Notifications / enquiries / forms ---------------------------------
const notifications = Array.from({ length: 12 }, (_, i) => ({
  publicId: `not_${i}_sample`,
  title: [
    "Training location changed",
    "Match selection published",
    "New assessment available",
    "Availability request",
    "Kit collection reminder",
    "Media day invitation",
  ][i % 6],
  message: "Please check the affected details in the relevant section of your portal.",
  createdAt: iso(-60 * (i * 3 + 1)),
  read: i > 3,
  channel: (["in_app", "email", "sms"] as const)[i % 3],
}));

const enquiries = Array.from({ length: 14 }, (_, i) => ({
  publicId: `enq_${i}_sample`,
  type: (
    [
      "Trial application",
      "Sponsorship",
      "Media enquiry",
      "General enquiry",
      "Academy application",
    ] as const
  )[i % 5],
  name: `${PLAYER_ROSTER[i % PLAYER_ROSTER.length].first} ${PLAYER_ROSTER[i % PLAYER_ROSTER.length].last}`,
  email: `enquirer${i + 1}@sample.local`,
  subject: [
    "Trial request",
    "Partnership opportunity",
    "Press accreditation",
    "Ticket question",
    "Academy trial",
  ][i % 5],
  message:
    "Sample enquiry text. This is a mock record used to design the enquiry management workflows.",
  status: (["new", "in_progress", "closed"] as const)[i % 3],
  createdAt: iso(-60 * (i * 5 + 4)),
}));

const formsCatalogue = [
  { publicId: "frm_trial", slug: "trial", title: "Player Trial Application", submissions: 47 },
  { publicId: "frm_academy", slug: "academy", title: "Academy Application", submissions: 84 },
  {
    publicId: "frm_sponsorship",
    slug: "sponsorship",
    title: "Sponsorship Enquiry",
    submissions: 12,
  },
  { publicId: "frm_media", slug: "media", title: "Media Enquiry", submissions: 21 },
];

// ---- Audit log ---------------------------------------------------------
const auditLogs = Array.from({ length: 30 }, (_, i) => ({
  publicId: `aud_${i}_sample`,
  actor: ["Thabo Ndlovu", "Sipho Mokoena", "Nadia Petersen", "Nomsa Khumalo"][i % 4],
  action: [
    "published article",
    "updated player profile",
    "changed lineup",
    "started stream",
    "ended stream",
    "created user",
    "changed role",
    "archived gallery",
    "published fixture",
    "recorded attendance",
  ][i % 10],
  target: ["Article #" + (i + 1), "Player #" + ((i % 25) + 1), "Match #" + (i + 1)][i % 3],
  createdAt: iso(-60 * (i * 7 + 2)),
  ipAddress: `10.0.0.${(i % 254) + 1}`,
}));

// ---- Analytics ---------------------------------------------------------
const analytics = {
  visitors7d: Array.from({ length: 7 }, (_, i) => ({
    date: new Date(now.getTime() - (6 - i) * 86400000).toISOString().slice(0, 10),
    visitors: 3200 + i * 480 + (i % 2 === 0 ? 500 : 0),
    pageviews: 8100 + i * 900,
  })),
  streamViewers: Array.from({ length: 24 }, (_, i) => ({
    minute: i * 5,
    concurrent: Math.round(600 + Math.sin(i / 3) * 250 + i * 12),
  })),
  topContent: [
    { title: "Champions of Soweto: how this squad has redefined the club", views: 24310 },
    { title: "Match preview: Orlando Athletic under Soweto lights", views: 18452 },
    { title: "Dlamini extends contract in landmark deal for the club", views: 15022 },
    { title: "Kit launch: 2025/26 home shirt unveiled", views: 12290 },
    { title: "Interview: Zulu on captaincy, culture and the road ahead", views: 9812 },
  ],
  sponsorImpressions: [
    { sponsor: "Rand Central Bank", impressions: 512000, clicks: 4820 },
    { sponsor: "Soweto Motors Group", impressions: 328000, clicks: 3110 },
    { sponsor: "Kliptown Energy", impressions: 214000, clicks: 1930 },
    { sponsor: "Vuka Insurance", impressions: 198000, clicks: 1610 },
    { sponsor: "Baobab Media", impressions: 140000, clicks: 1150 },
  ],
};

// ---- Live state builder ------------------------------------------------
function buildLiveState(matchId: string): MatchLiveState {
  return {
    matchPublicId: matchId,
    status: "live",
    serverTimestamp: new Date().toISOString(),
    officialMatchSeconds: 62 * 60 + 12,
    period: "second_half",
    clockRunning: true,
    score: liveFixture.score ?? { home: 2, away: 1 },
    latestEvents: LIVE_EVENTS.map((e, i) => ({
      publicId: `evt_${i}_sample`,
      minute: e.minute,
      type: e.type as MatchLiveState["latestEvents"][number]["type"],
      team: e.team as "home" | "away",
      playerName: e.playerName,
      detail: e.detail,
    })),
    latestCommentary: [
      {
        publicId: "cmt_1",
        minute: 62,
        timestamp: iso(-3),
        text: "GREAT SAVE! Sanele Mabaso claws it away at the near post.",
        important: true,
      },
      {
        publicId: "cmt_2",
        minute: 60,
        timestamp: iso(-5),
        text: "Corner won by Ndlovu after a mazy run down the right.",
      },
      {
        publicId: "cmt_3",
        minute: 58,
        timestamp: iso(-7),
        text: "Sibiya on for Nkosi. Fresh legs up top for Soweto.",
      },
      {
        publicId: "cmt_4",
        minute: 46,
        timestamp: iso(-19),
        text: "We're back underway at Soweto Stadium.",
      },
    ],
    statisticsSummary: {
      possession: { home: 58, away: 42 },
      shots: { home: 12, away: 6 },
      shotsOnTarget: { home: 6, away: 2 },
      corners: { home: 7, away: 3 },
      fouls: { home: 9, away: 13 },
      yellowCards: { home: 1, away: 3 },
      redCards: { home: 0, away: 0 },
      offsides: { home: 2, away: 1 },
    },
    stream: { publicId: "stm_live_sample", status: "live", viewerCount: 1287 },
  };
}

function buildMatchDetail(matchId: string): MatchDetail {
  const base =
    fixtures.find((f) => f.publicId === matchId) ??
    results.find((f) => f.publicId === matchId) ??
    liveFixture;
  const live = buildLiveState(matchId);
  return {
    ...base,
    events: live.latestEvents,
    statistics: live.statisticsSummary,
    commentary: live.latestCommentary,
    lineups: {
      home: {
        formation: "4-3-3",
        starters: players.slice(0, 11).map((p) => ({
          playerName: p.displayName,
          shirtNumber: p.shirtNumber ?? undefined,
          position: p.position,
        })),
        substitutes: players
          .slice(11, 18)
          .map((p) => ({ playerName: p.displayName, shirtNumber: p.shirtNumber ?? undefined })),
        coach: "Sipho Mokoena",
      },
      away: {
        formation: "4-4-2",
        starters: Array.from({ length: 11 }, (_, i) => ({
          playerName: `Away Player ${i + 1}`,
          shirtNumber: i + 1,
          position: ["GK", "RB", "CB", "CB", "LB", "RM", "CM", "CM", "LM", "ST", "ST"][i],
        })),
        substitutes: Array.from({ length: 7 }, (_, i) => ({
          playerName: `Away Sub ${i + 1}`,
          shirtNumber: 12 + i,
        })),
        coach: "Opposition Coach",
      },
    },
  };
}

// ---- Export ------------------------------------------------------------
export const mockData = {
  demoUsers,
  session: demoUsers.admin as AuthenticatedUser | null,
  settings,
  navigation,
  home,
  teams,
  players,
  staff,
  liveFixture,
  fixtures,
  results,
  streams,
  articles,
  galleries,
  videos,
  sponsors,
  leagueTables,
  portalUsers: PORTAL_USERS,
  trainingSessions,
  trainingAttendance,
  playerAssessments,
  notifications,
  enquiries,
  formsCatalogue,
  auditLogs,
  analytics,
  buildLiveState,
  buildMatchDetail,
};

export const OPPONENT_NAMES = OPPONENTS;
